import { useApolloClient } from '@vue/apollo-composable'
import { computed, type ComputedRef, getCurrentInstance, nextTick, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import type { LegalConsentsValues } from '@/components/ai/onboarding/OnboardingLegalConsents.vue'
import { useBranding } from '@/composables/useBranding'
import { refetchCompanyStateInApolloCache, syncCompanyStateInApolloCache } from '@/lib/companyState'
import bus, { eventNames } from '@/lib/eventBus'
import featureNames from '@/lib/featureNames'
import { email } from '@/lib/validation'
import UPDATE_COMPANY_AMBASSADOR from '@/modules/company/queries/UpdateAmbassador.gql'
import UPDATE_COMPANY from '@/modules/company/queries/UpdateCompany.gql'
import UPDATE_COMPANY_AUTOMATIC_INTEGRATION from '@/modules/company/queries/UpdateCompanyAutomaticIntegration.gql'
import UPDATE_COMPANY_CONTACT_PERSON from '@/modules/company/queries/UpdateCompanyContactPerson.gql'
import UPDATE_COMPANY_THEME from '@/modules/company/queries/UpdateCompanyTheme.gql'
import UPDATE_ONBOARDING_SLOGAN from '@/modules/company/queries/UpdateSlogan.gql'
import { legalConsentsFields } from '@/modules/iubenda/legalConsentsFields'
import { persistLegalConsents } from '@/modules/onboarding/legalConsentsPersistence'
import { clearOnboardingProgress } from '@/modules/onboarding/lib/onboardingProgressStorage'
import SUBMIT_ONBOARDING from '@/modules/onboarding/queries/SubmitOnboarding.gql'
import COMPLETE_ONBOARDING_STEP_CRM_CONFIG from '@/modules/registration/queries/CompleteOnboardingStepCRMConfig.gql'
import COMPLETE_ONBOARDING_STEP_WEBSITE from '@/modules/registration/queries/CompleteOnboardingStepWebsite.gql'
import UPDATE_COMPANY_META from '@/modules/settings/companySettings/queries/UpdateCompanyMetaData.gql'
import UPDATE_LICENSE_AGREEMENT from '@/queries/UpdateLicenseAgreement.gql'
import { useAiAssistantStore } from '@/stores/aiAssistant'

import { isCommissionInputValid } from '../lib/onboardingCommission'
import { legalLinkForSubmit, normalizeHttpsUrl } from '../lib/onboardingLegalUrls'
import {
  buildAmbassadorInput,
  buildCompanyCoreInput,
  buildGeneralContactInput,
  buildMetaInput,
  buildSloganValue,
  buildThemeColors,
} from '../lib/onboardingMutationInputs'
import type { OnboardingFormData } from '../lib/onboardingPrefill'
import { useOnboardingFeatures } from './useOnboardingFeatures'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CompanyData = Record<string, any>
type Translate = (key: string, ...args: unknown[]) => string

export const EMPTY_LEGAL_CONSENTS: LegalConsentsValues = {
  acceptAgb: false,
  acceptPrivacy: false,
  acceptGuideline: false,
  contactViaEmail: false,
  contactViaWhatsapp: false,
  acceptConfirmData: false,
}

export function useOnboardingSubmit(options: {
  company: ComputedRef<CompanyData | null>
  formData: OnboardingFormData
  userEditedFields: Set<string>
  aiCapturedFields: Set<string>
  hasMissingOnboardingData: ComputedRef<boolean>
  missingOnboardingDataSummary: ComputedRef<string>
  isUsOnboarding?: ComputedRef<boolean>
  t: Translate
  refetchCompany: () => Promise<unknown>
  countryValueForSubmit: () => string | null
  // Flush pending per-field autosaves before the final submit reads company data.
  flushFieldAutosaves?: () => Promise<void>
  // The GENERAL contact id an autosave may have created, to avoid duplicates.
  getAutosavedContactId?: () => string | undefined
}) {
  const {
    company,
    formData,
    userEditedFields,
    aiCapturedFields,
    hasMissingOnboardingData,
    missingOnboardingDataSummary,
    isUsOnboarding,
    t,
    refetchCompany,
    countryValueForSubmit,
    flushFieldAutosaves,
    getAutosavedContactId,
  } = options
  const { isFeatureActive, getFeature } = useOnboardingFeatures()
  const { legalCompanyName, readablePlatformName, publicTOCandLicenceUrl } = useBranding()
  const { resolveClient } = useApolloClient()
  const router = useRouter()
  const instance = getCurrentInstance()
  const reviewConsents = reactive<LegalConsentsValues>({ ...EMPTY_LEGAL_CONSENTS })
  const isSubmitting = ref(false)
  const postOnboardingRedirectLoading = ref(false)
  const widgetIntegrationBusy = ref(false)

  const requiresAgbAcceptance = computed(() => {
    if (company.value?.licenseAgreement?.acceptedAt) return false
    if (!isFeatureActive(featureNames.DATA_PRIVACY)) return false
    return !!getFeature(featureNames.DATA_PRIVACY)?.config?.hasConditions
  })
  const reviewAcceptancesValid = computed(() => {
    const acceptAgb = !requiresAgbAcceptance.value || reviewConsents.acceptAgb
    const acceptGuideline =
      !isFeatureActive(featureNames.MATERIAL_GUIDE) || reviewConsents.acceptGuideline
    return (
      acceptAgb &&
      reviewConsents.acceptPrivacy &&
      acceptGuideline &&
      reviewConsents.acceptConfirmData
    )
  })
  const showSupportAndTipsHint = computed(
    () =>
      reviewAcceptancesValid.value &&
      !reviewConsents.contactViaEmail &&
      !reviewConsents.contactViaWhatsapp
  )
  const showMaklerMittelpunktOffer = computed(
    () =>
      isFeatureActive(featureNames.MAKLER_MITTEL) &&
      !aiCapturedFields.has('automaticIntegration') &&
      !userEditedFields.has('automaticIntegration')
  )
  const widgetInitialValue = computed(() => {
    if (formData.automaticIntegration === true) return true
    if (formData.automaticIntegration === false && aiCapturedFields.has('automaticIntegration')) {
      return false
    }
    return null
  })

  function applyConsents(source: LegalConsentsValues | null | undefined) {
    if (!source) return
    Object.assign(reviewConsents, source)
  }

  /**
   * Review and the enter-data form share `reviewConsents`. Chat answers are
   * copied in when present. Never reset to empty — that wiped checkboxes the
   * user already accepted on the form.
   */
  function hydrateConsentsFromChat() {
    applyConsents(useAiAssistantStore().chatLegalConsents)
  }

  watch(
    () => useAiAssistantStore().chatLegalConsents,
    (chatConsents) => applyConsents(chatConsents),
    { immediate: true }
  )

  function resolveCountryCodeForOnboardingStep() {
    const fromForm = countryValueForSubmit()
    if (fromForm && /^[a-zA-Z]{2}$/.test(String(fromForm))) return String(fromForm).toLowerCase()
    const legacy = (company.value?.address?.country || '').trim()
    if (legacy && /^[a-zA-Z]{2}$/.test(legacy)) return legacy.toLowerCase()
    return 'de'
  }

  async function persistReviewAcceptances() {
    const client = resolveClient()
    if (requiresAgbAcceptance.value && reviewConsents.acceptAgb && company.value?.id) {
      try {
        await client.mutate({
          mutation: UPDATE_LICENSE_AGREEMENT,
          variables: { companyId: company.value.id },
        })
      } catch (err) {
        console.error('[Onboarding] Failed to accept license agreement:', err)
        throw err
      }
    }
    const chatLegalConsents = useAiAssistantStore().chatLegalConsents
    const chatConsentsAreCurrent = Boolean(
      chatLegalConsents &&
      legalConsentsFields.every(({ key }) => chatLegalConsents[key] === reviewConsents[key])
    )

    await persistLegalConsents({
      mutate: (options) => client.mutate(options),
      companyId: company.value?.id,
      values: reviewConsents,
      requiresAgb: requiresAgbAcceptance.value,
      requiresGuideline: isFeatureActive(featureNames.MATERIAL_GUIDE),
      proofTextContext: {
        t,
        legalCompanyName: legalCompanyName.value,
        readablePlatformName: readablePlatformName.value,
        publicTOCandLicenceUrl: publicTOCandLicenceUrl.value,
      },
      persistAgbAcceptance: false,
      // Avoid a duplicate only while the values still match the record created
      // by ChatLegalConsents. Changes made in OnboardingLegalConsents or
      // OnboardingReviewConsents must produce a fresh record on completion.
      submitIubendaRecord: !chatConsentsAreCurrent,
    })
  }

  async function advanceSelfOnboardingMachineBeforeSubmit({
    websiteNorm,
    imprint,
    privacy,
  }: {
    websiteNorm: string | null
    imprint: string | null
    privacy: string | null
  }) {
    const values = company.value?.state?.stringValues
    const isSelf = Array.isArray(values) && values.includes('SELF_ONBOARDING')
    if (!isSelf) return { ok: true as const }
    if (values?.includes('ONBOARDING')) return { ok: true as const }
    const countryCode = resolveCountryCodeForOnboardingStep()
    const hasNoInitialWebsite = !(formData.website || '').trim()
    const client = resolveClient()
    try {
      if (!hasNoInitialWebsite && !websiteNorm) {
        return { ok: false as const, message: t('registration.website.errors.invalid-link') }
      }
      if (!hasNoInitialWebsite && websiteNorm) {
        const { data } = await client.mutate({
          mutation: COMPLETE_ONBOARDING_STEP_WEBSITE,
          variables: {
            input: {
              hasNoInitialWebsite: false,
              countryCode,
              links: {
                website: websiteNorm,
                imprint: imprint || websiteNorm,
                privacy: privacy || websiteNorm,
              },
            },
          },
        })
        const webRes = data?.completeOnboardingStepWebsite
        if (webRes?.status === 'ERROR' && webRes.errorCode === 'INVALID_WEBSITE_LINKS') {
          return { ok: false as const, message: t('registration.website.errors.invalid-link') }
        }
        if (webRes?.status === 'ERROR') {
          console.warn(
            '[Onboarding] completeOnboardingStepWebsite:',
            webRes.errorCode,
            webRes.message
          )
        }
      } else if (hasNoInitialWebsite) {
        const { data } = await client.mutate({
          mutation: COMPLETE_ONBOARDING_STEP_WEBSITE,
          variables: { input: { hasNoInitialWebsite: true, countryCode } },
        })
        const webRes = data?.completeOnboardingStepWebsite
        if (webRes?.status === 'ERROR' && webRes.errorCode === 'INVALID_WEBSITE_LINKS') {
          return { ok: false as const, message: t('registration.website.errors.invalid-link') }
        }
        if (webRes?.status === 'ERROR') {
          console.warn(
            '[Onboarding] completeOnboardingStepWebsite (no website):',
            webRes.errorCode,
            webRes.message
          )
        }
      }
      await refetchCompany()
      if (company.value?.state?.stringValues?.includes('ONBOARDING')) return { ok: true as const }
      const { data: crmData } = await client.mutate({
        mutation: COMPLETE_ONBOARDING_STEP_CRM_CONFIG,
      })
      const crmRes = crmData?.completeOnboardingStepCRMConfig
      if (crmRes?.status === 'ERROR') {
        console.warn(
          '[Onboarding] completeOnboardingStepCRMConfig:',
          crmRes.errorCode,
          crmRes.message
        )
      }
      return { ok: true as const }
    } catch (err) {
      console.error('[Onboarding] advance machine step failed:', err)
      return { ok: false as const, message: t('onboarding.wizard.review.onboarding-step-error') }
    }
  }

  async function handleReviewWidgetIntegration({ enabled }: { enabled: boolean }) {
    if (!company.value?.id) return
    widgetIntegrationBusy.value = true
    try {
      await resolveClient().mutate({
        mutation: UPDATE_COMPANY_AUTOMATIC_INTEGRATION,
        variables: {
          input: { companyId: company.value.id, automaticIntegration: Boolean(enabled) },
        },
      })
      formData.automaticIntegration = Boolean(enabled)
      userEditedFields.add('automaticIntegration')
      aiCapturedFields.add('automaticIntegration')
      if (enabled) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(instance?.proxy as any)?.$tracking?.event?.(
          'Onboarding',
          'Selected',
          'Makler Mittel Partner'
        )
      }
      const store = useAiAssistantStore()
      if (store.onboardingEmbeddedChatActive) {
        try {
          await store.applyOnboardingWidgetTool('widget_integration', {
            form_key: 'automaticIntegration',
            enabled: Boolean(enabled),
            user_message: enabled
              ? t('onboarding.publication.widget-integration-checkbox')
              : t('onboarding.wizard.chat-widgets.widget-integration-decline'),
          })
        } catch (syncErr) {
          console.warn('[Onboarding] widget_integration session sync skipped', syncErr)
        }
      }
    } catch (e) {
      console.warn('[Onboarding] failed to save automaticIntegration', e)
      bus.$emit(eventNames.SHOW_SNACKBAR, { color: 'error', text: t('alerts.general.error') })
    } finally {
      widgetIntegrationBusy.value = false
    }
  }

  async function handleComplete() {
    if (!company.value) return
    if (!reviewAcceptancesValid.value) {
      bus.$emit(eventNames.SHOW_SNACKBAR, {
        color: 'warning',
        text: t('onboarding.wizard.review.accept-required'),
      })
      return
    }
    if (
      email(formData.contactEmail.trim()) !== true ||
      email(formData.ambassadorEmail.trim()) !== true
    ) {
      bus.$emit(eventNames.SHOW_SNACKBAR, {
        color: 'warning',
        text: t('labels.email-invalid-validation'),
      })
      return
    }
    if (!isCommissionInputValid(formData.commission)) {
      bus.$emit(eventNames.SHOW_SNACKBAR, {
        color: 'warning',
        text: t('onboarding.wizard.review.invalid-commission'),
      })
      return
    }
    if (hasMissingOnboardingData.value) {
      bus.$emit(eventNames.SHOW_SNACKBAR, {
        color: 'warning',
        text: t('onboarding.wizard.review.missing-fields-hint', {
          fields: missingOnboardingDataSummary.value,
        }),
      })
      return
    }
    isSubmitting.value = true
    const client = resolveClient()
    try {
      // Settle any in-flight per-field autosaves and refetch so the final submit
      // builds on the freshest company record. Otherwise an autosave that just
      // created the GENERAL contact would be invisible here and we'd upsert a
      // second, duplicate id-less contact.
      if (flushFieldAutosaves) {
        await flushFieldAutosaves()
        await refetchCompany()
      }
      await persistReviewAcceptances()
      const websiteNorm = normalizeHttpsUrl(formData.website)
      const imprint = legalLinkForSubmit(formData.linkImprint, websiteNorm)
      const privacy = legalLinkForSubmit(formData.linkPrivacy, websiteNorm)
      await client.mutate({
        mutation: UPDATE_COMPANY,
        variables: {
          input: buildCompanyCoreInput(formData, company.value, {
            isUsOnboarding: Boolean(isUsOnboarding?.value),
            countryValueForSubmit,
          }),
        },
      })
      const colors = buildThemeColors(formData, company.value)
      if (colors) {
        await client.mutate({
          mutation: UPDATE_COMPANY_THEME,
          variables: { input: { companyId: company.value.id, colors } },
        })
      }
      const slogan = buildSloganValue(formData, company.value)
      if (slogan != null) {
        await client.mutate({
          mutation: UPDATE_ONBOARDING_SLOGAN,
          variables: { input: { companyId: company.value.id, slogan } },
        })
      }
      const metaInput = buildMetaInput(formData, company.value)
      if (metaInput) {
        await client.mutate({
          mutation: UPDATE_COMPANY_META,
          variables: { input: metaInput },
        })
      }
      await client.mutate({
        mutation: UPDATE_COMPANY_AMBASSADOR,
        variables: { input: buildAmbassadorInput(formData, company.value) },
      })
      const { input: generalContactInput, existingId: generalContactId } = buildGeneralContactInput(
        formData,
        company.value
      )
      // Prefer the refetched company's contact id; fall back to one an autosave
      // created (in case the refetch hasn't surfaced it yet) so we update rather
      // than create a duplicate GENERAL contact.
      const contactId = generalContactId || getAutosavedContactId?.()
      if (contactId) {
        await client.mutate({
          mutation: UPDATE_COMPANY_CONTACT_PERSON,
          variables: { input: { id: contactId, ...generalContactInput } },
        })
      } else {
        // Same mutation upserts: omitting id creates the GENERAL contact.
        const { data: createdContact } = await client.mutate({
          mutation: UPDATE_COMPANY_CONTACT_PERSON,
          variables: { input: generalContactInput },
        })
        if (!createdContact?.contactPerson?.id) {
          throw new Error('[Onboarding] Failed to create GENERAL contact person.')
        }
      }
      const machine = await advanceSelfOnboardingMachineBeforeSubmit({
        websiteNorm,
        imprint,
        privacy,
      })
      if (!machine.ok) {
        bus.$emit(eventNames.SHOW_SNACKBAR, { color: 'error', text: machine.message })
        return
      }
      await refetchCompany()
      const { data: submitData } = await client.mutate({
        mutation: SUBMIT_ONBOARDING,
        variables: { companyId: company.value.id },
      })
      // Only fired once submitOnboarding actually succeeds — not on the button
      // click — so failed attempts (e.g. missing required field) aren't counted.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(instance?.proxy as any)?.$tracking?.heapEvent?.('onboarding_completed')
      const activeState = submitData?.state?.state
      if (activeState?.length) {
        syncCompanyStateInApolloCache(client, company.value.id, activeState)
      }
      await refetchCompanyStateInApolloCache(client, company.value.id)
      useAiAssistantStore().setOnboardingEmbeddedChatActive(false)
      clearOnboardingProgress(company.value.id)
      await refetchCompany()
      bus.$emit(eventNames.SHOW_SNACKBAR, {
        color: 'success',
        text: t('onboarding.wizard.done.title'),
      })
      postOnboardingRedirectLoading.value = true
      await nextTick()
      await new Promise((resolve) => setTimeout(resolve, 750))
      window.location.assign(router.resolve({ name: 'dashboard' }).href)
    } catch (err) {
      console.error('[Onboarding] Submit failed:', err)
      const gqlCode = (err as { graphQLErrors?: { extensions?: { code?: string } }[] })
        ?.graphQLErrors?.[0]?.extensions?.code
      bus.$emit(eventNames.SHOW_SNACKBAR, {
        color: 'error',
        text:
          gqlCode === 'BAD_USER_INPUT'
            ? t('onboarding.wizard.review.submit-blocked-hint')
            : t('onboarding.stepContainer.saveError'),
      })
    } finally {
      isSubmitting.value = false
    }
  }

  return {
    reviewConsents,
    isSubmitting,
    postOnboardingRedirectLoading,
    widgetIntegrationBusy,
    requiresAgbAcceptance,
    reviewAcceptancesValid,
    showSupportAndTipsHint,
    showMaklerMittelpunktOffer,
    widgetInitialValue,
    hydrateConsentsFromChat,
    handleReviewWidgetIntegration,
    handleComplete,
  }
}
